import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  FormControlLabel,
  Checkbox,
  FormGroup,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  TextField,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import GetAppIcon from '@mui/icons-material/GetApp';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { authFetch } from '../api/authFetch';
import { useAuth } from '../contexts/AuthContext';
import MermaidDiagram from './MermaidDiagram';

interface QuestionOption {
  value: number;
  label: string;
}

interface Question {
  id: string;
  question: string;
  description: string;
  options: QuestionOption[];
}

interface QuestionCategory {
  name: string;
  description: string;
  questions: Question[];
}

interface AssessmentQuestions {
  name: string;
  description: string;
  categories: Record<string, QuestionCategory>;
}

interface AssessmentFlowProps {
  category: string;
  domain: string;
  onBack: () => void;
  /** If resuming from a saved draft, pass the draft ID + saved state */
  draftId?: string;
  savedAnswers?: Record<string, { selectedOptions: number[]; comment: string }>;
  savedOrganizationInfo?: OrganizationInfo;
  savedStep?: number;
  requestId?: string;
  /** Hide the "Export to Excel" button (e.g. in client portal) */
  hideExcelExport?: boolean;
}

interface UserAnswer {
  selectedOptions: number[];
  comment: string;
}

interface UserAnswers {
  [questionId: string]: UserAnswer;
}

interface OrganizationInfo {
  organizationName: string;
  contactEmail: string;
  industry: string;
  organizationSize: string;
  yearsOfExperience: string;
}

interface AssessmentResult {
  assessmentId: string;
  result: {
    category: string;
    domain: string;
    items: Array<{
      id: string;
      question: string;
      score: number;
      rationale: string;
      recommendation: string;
      riskLevel: string;
      references: string[];
    }>;
    domainScore: number;
    summary: string;
    recommendations: string[];
    mermaidDiagram?: string;
    metadata: {
      agentId: string;
      agentName: string;
      modelDeployment: string;
      timestamp: string;
      category: string;
      domain: string;
    };
  };
}

const AssessmentFlow: React.FC<AssessmentFlowProps> = ({
  category, domain, onBack,
  draftId: initialDraftId, savedAnswers, savedOrganizationInfo, savedStep, requestId,
  hideExcelExport,
}) => {
  const { organizationData } = useAuth();
  const [currentStep, setCurrentStep] = useState(savedStep ?? 0);
  const [organizationInfo, setOrganizationInfo] = useState<OrganizationInfo>(
    savedOrganizationInfo ?? {
      organizationName: '',
      contactEmail: '',
      industry: '',
      organizationSize: '',
      yearsOfExperience: ''
    }
  );
  const [questions, setQuestions] = useState<AssessmentQuestions | null>(null);
  const [userAnswers, setUserAnswers] = useState<UserAnswers>(savedAnswers ?? {});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AssessmentResult | null>(null);
  const [expandedCategory, setExpandedCategory] = useState<string | false>(false);
  
  // Assessment control state
  const [isPaused, setIsPaused] = useState(false);
  const [assessmentController, setAssessmentController] = useState<AbortController | null>(null);
  
  // SME submission state
  const [smeSubmitStatus, setSmeSubmitStatus] = useState<'idle' | 'submitting' | 'submitted'>('idle');
  const [smeSubmitError, setSmeSubmitError] = useState<string | null>(null);

  // Save/Resume state
  const [draftId, setDraftId] = useState<string | undefined>(initialDraftId);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<string | null>(null);

  // Platform category state (architecture generation only)
  const [platformCategories, setPlatformCategories] = useState<Array<{value: string; label: string; description: string}>>([]);
  const [platformFields, setPlatformFields] = useState<Record<string, any[]>>({});
  const [selectedPlatform, setSelectedPlatform] = useState<string>('');
  const [architectureRawFields, setArchitectureRawFields] = useState<any[]>([]);

  // Auto-populate organization name and email from logged-in user profile
  const isArchitecture = domain === 'Architecture Generation';

  // Rebuild architecture questions whenever the user picks a different platform
  useEffect(() => {
    if (!isArchitecture || !selectedPlatform || architectureRawFields.length === 0) return;

    const platformSpecific = platformFields[selectedPlatform] || [];

    const platformLabel = platformCategories.find(p => p.value === selectedPlatform)?.label || 'Platform Services';

    const updatedQuestions: AssessmentQuestions = {
      name: "Baseline Architecture Generation",
      description: `Blueprint architecture for ${platformLabel}`,
      categories: {} as Record<string, QuestionCategory>,
    };

    // Platform-specific fields as their own category (shown first)
    if (platformSpecific.length > 0) {
      updatedQuestions.categories['platform_services'] = {
        name: platformLabel,
        description: `Services and workloads specific to ${platformLabel}.`,
        questions: platformSpecific.map((field: any) => ({
          id: field.id,
          question: field.label,
          description: `Select ${field.label.toLowerCase()}`,
          options: field.options.map((option: any, index: number) => ({
            value: index + 1,
            label: option.label,
          })),
        })),
      };
    }

    // Common infrastructure fields
    updatedQuestions.categories['architecture_config'] = {
      name: 'Infrastructure Configuration',
      description: 'Select the common infrastructure, security, and deployment settings.',
      questions: architectureRawFields.map((field: any) => ({
        id: field.id,
        question: field.label,
        description: `Select ${field.label.toLowerCase()}`,
        options: field.options.map((option: any, index: number) => ({
          value: index + 1,
          label: option.label,
        })),
      })),
    };

    setQuestions(updatedQuestions);
    setExpandedCategory('platform_services');
  }, [selectedPlatform]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (organizationData) {
      setOrganizationInfo(prev => ({
        ...prev,
        organizationName: prev.organizationName || organizationData.organizationName || '',
        contactEmail: prev.contactEmail || organizationData.userEmail || '',
      }));
    }
  }, [organizationData]);

  // Auto-load questions when resuming a saved draft (step 2 = answering)
  useEffect(() => {
    if (savedAnswers && (savedStep === 2 || savedStep === 1)) {
      loadQuestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveProgress = async () => {
    try {
      setSaving(true);
      setSaveMessage(null);
      const { answered, total } = getAnsweredCount();
      const res = await authFetch('/api/client/assessment-drafts', {
        method: 'POST',
        body: JSON.stringify({
          draft_id: draftId,
          request_id: requestId,
          category,
          domain,
          answers: userAnswers,
          organization_info: organizationInfo,
          current_step: currentStep,
          total_questions: total,
          answered_count: answered,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setDraftId(data.draft_id);
        setLastSaved(new Date().toLocaleTimeString());
        setSaveMessage(`Progress saved — expires ${new Date(data.expires_at).toLocaleDateString()}`);
      } else {
        setSaveMessage(data.error || 'Failed to save progress');
      }
    } catch {
      setSaveMessage('Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  const deleteDraft = async () => {
    if (!draftId) return;
    try {
      await authFetch(`/api/client/assessment-drafts/${draftId}`, { method: 'DELETE' });
      setDraftId(undefined);
    } catch { /* best-effort */ }
  };

  const steps = isArchitecture
    ? [
        'Organization Info',
        'Load Configuration',
        'Configure Architecture',
        'Generate Blueprint',
        'View Blueprint',
        'Submit to SME'
      ]
    : [
        'Organization Info', 
        'Load Questions', 
        'Answer Questions', 
        'Run Assessment', 
        'View Results', 
        'Submit to SME'
      ];

  const loadQuestions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await authFetch(`/api/assessments/questions?domain=${encodeURIComponent(domain)}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load questions');
      }
      
      // Handle Architecture Generation differently
      if (data.type === 'architecture_generation' && data.fields) {
        // Store platform data for the dropdown
        if (data.platform_categories) {
          setPlatformCategories(data.platform_categories);
          setPlatformFields(data.platform_fields || {});
          setArchitectureRawFields(data.fields);
        }
        // Build initial questions using only common fields
        const allFields = data.fields || [];
        const architectureQuestions: AssessmentQuestions = {
          name: "Baseline Architecture Generation",
          description: data.description,
          categories: {
            "architecture_config": {
              name: "Infrastructure Configuration",
              description: "Select the common infrastructure, security, and deployment settings.",
              questions: allFields.map((field: any) => ({
                id: field.id,
                question: field.label,
                description: `Select ${field.label.toLowerCase()}`,
                options: field.options.map((option: any, index: number) => ({
                  value: index + 1,
                  label: option.label
                }))
              }))
            }
          }
        };
        setQuestions(architectureQuestions);
        // Auto-expand the architecture config category
        setExpandedCategory("architecture_config");
      } else {
        setQuestions(data.questions);
        // Auto-expand first category for regular assessments
        if (data.questions?.categories) {
          const firstCategoryKey = Object.keys(data.questions.categories)[0];
          if (firstCategoryKey) {
            setExpandedCategory(firstCategoryKey);
          }
        }
      }
      
      setCurrentStep(2); // Move to answering questions
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleOrganizationInfoChange = (field: keyof OrganizationInfo, value: string) => {
    setOrganizationInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const isOrganizationInfoComplete = () => {
    return Object.values(organizationInfo).every(value => value.trim() !== '');
  };

  const proceedToQuestions = () => {
    if (isOrganizationInfoComplete()) {
      setCurrentStep(1); // Move to loading questions
      loadQuestions();
    }
  };

  const handleOptionToggle = (questionId: string, value: number) => {
    setUserAnswers(prev => {
      const current = prev[questionId] || { selectedOptions: [], comment: '' };
      const selected = current.selectedOptions.includes(value)
        ? current.selectedOptions.filter(v => v !== value)
        : [...current.selectedOptions, value].sort((a, b) => a - b);
      return {
        ...prev,
        [questionId]: { ...current, selectedOptions: selected }
      };
    });
  };

  const handleCommentChange = (questionId: string, comment: string) => {
    setUserAnswers(prev => {
      const current = prev[questionId] || { selectedOptions: [], comment: '' };
      return {
        ...prev,
        [questionId]: { ...current, comment }
      };
    });
  };

  const getAnsweredCount = () => {
    if (!questions || !questions.categories) return { answered: 0, total: 0 };
    
    let totalQuestions = 0;
    Object.values(questions.categories).forEach(category => {
      totalQuestions += category.questions?.length || 0;
    });
    
    return {
      answered: Object.values(userAnswers).filter(a => a.selectedOptions.length > 0).length,
      total: totalQuestions
    };
  };

  const runAssessment = async () => {
    try {
      setLoading(true);
      setError(null);
      setIsPaused(false);
      setCurrentStep(3); // Move to running assessment
      
      // Create abort controller for cancellation support
      const controller = new AbortController();
      setAssessmentController(controller);
      
      // Convert answers to the format expected by the backend
      // Build a lookup map from questionId → question text
      const questionTextMap: Record<string, string> = {};
      if (questions?.categories) {
        Object.values(questions.categories).forEach((cat) => {
          cat.questions.forEach((q) => {
            questionTextMap[q.id] = q.question;
          });
        });
      }

      // For architecture generation, build index→value reverse map so the
      // backend receives actual option values (e.g. "app_service") instead
      // of numeric indices.
      let archOptionMap: Record<string, Record<number, string>> = {};
      if (isArchitecture) {
        const allArchFields = [
          ...(platformFields[selectedPlatform] || []),
          ...architectureRawFields,
        ];
        for (const field of allArchFields) {
          const fieldMap: Record<number, string> = {};
          (field.options || []).forEach((opt: any, idx: number) => {
            fieldMap[idx + 1] = opt.value; // UI stores index+1
          });
          archOptionMap[field.id] = fieldMap;
        }
      }

      const answers = Object.entries(userAnswers)
        .filter(([, answer]) => answer.selectedOptions.length > 0)
        .map(([questionId, answer]) => {
          // Resolve architecture option values from indices
          if (isArchitecture && archOptionMap[questionId]) {
            const fieldMap = archOptionMap[questionId];
            const resolvedValues = answer.selectedOptions.map(
              idx => fieldMap[idx] || String(idx)
            );
            // Single-select fields get a string, multi-select get an array
            const answerValue = resolvedValues.length === 1
              ? resolvedValues[0]
              : resolvedValues;
            return {
              questionId,
              question: questionTextMap[questionId] || questionId,
              selectedOptions: answer.selectedOptions,
              answer: answerValue,
              score: Math.max(...answer.selectedOptions),
              comment: answer.comment || '',
            };
          }
          return {
            questionId,
            question: questionTextMap[questionId] || questionId,
            selectedOptions: answer.selectedOptions,
            score: Math.max(...answer.selectedOptions),
            comment: answer.comment || '',
          };
        });
      
      const requestBody = {
        category,
        domain,
        answers,
        organizationInfo,
        context: `Assessment completed for ${organizationInfo.organizationName} (${organizationInfo.industry}) - ${domain} with ${answers.length} answered questions`
      };
      
      const response = await authFetch(`/api/assessments/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal // Add cancellation support
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Assessment failed');
      }
      
      setResult(data);
      setCurrentStep(4); // Move to results
      
      // Delete the draft after successful assessment completion
      deleteDraft();
      
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        setError('Assessment was cancelled');
      } else {
        setError(err instanceof Error ? err.message : 'Assessment failed');
      }
      setCurrentStep(2); // Go back to questions
    } finally {
      setLoading(false);
      setAssessmentController(null);
    }
  };

  const pauseAssessment = () => {
    setIsPaused(true);
    // Note: This is a UI pause only - backend continues processing
  };

  const resumeAssessment = () => {
    setIsPaused(false);
  };

  const stopAssessment = () => {
    if (assessmentController) {
      assessmentController.abort();
    }
    setLoading(false);
    setCurrentStep(2); // Go back to questions
  };

  const exportToExcel = async () => {
    if (!result) return;
    
    try {
      // Build query parameters with organization information
      const queryParams = new URLSearchParams({
        orgName: organizationInfo.organizationName,
        contactEmail: organizationInfo.contactEmail,
        industry: organizationInfo.industry,
        orgSize: organizationInfo.organizationSize,
        yearsExp: organizationInfo.yearsOfExperience
      });
      
      const response = await authFetch(`/api/assessments/${result.assessmentId}/export.xlsx?${queryParams}`);
      
      if (!response.ok) {
        if (response.status === 403) {
          setError('Your report is currently under expert review. The final PowerPoint report will be available for download on the Final Reports page after the review and playback session are completed.');
          return;
        }
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${domain.replace(/\s+/g, '_')}_Assessment_Results.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (err) {
      setError('Failed to export results. Please try again or contact support.');
    }
  };

  const submitToSME = async () => {
    if (!result) return;
    
    try {
      setSmeSubmitStatus('submitting');
      setSmeSubmitError(null);
      
      const response = await authFetch(`/api/client/assessments/${result.assessmentId}/submit-to-sme`, {
        method: 'POST',
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit to SME');
      }
      
      setSmeSubmitStatus('submitted');
      setCurrentStep(5); // Move to confirmation step
      
    } catch (err) {
      setSmeSubmitError(err instanceof Error ? err.message : 'Failed to submit to SME');
      setSmeSubmitStatus('idle');
    }
  };

  const renderQuestions = () => {
    if (!questions) return null;

    const answeredCount = getAnsweredCount();
    const { answered, total } = typeof answeredCount === 'object' ? answeredCount : { answered: 0, total: 0 };
    const progress = total > 0 ? (answered / total) * 100 : 0;

    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            {questions.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {questions.description}
          </Typography>
          
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="body2" gutterBottom>
                Progress: {answered} of {total} {isArchitecture ? 'selections' : 'questions answered'}
              </Typography>
              <LinearProgress variant="determinate" value={progress} sx={{ mt: 1 }} />
            </CardContent>
          </Card>
        </Box>

        {/* Platform Category Dropdown — shown only for Architecture Generation */}
        {isArchitecture && platformCategories.length > 0 && (
          <Card sx={{ mb: 3, border: '2px solid #00338D' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#00338D' }}>
                Select Target Platform
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose the technology platform to generate a tailored architecture blueprint.
                Platform-specific services will appear based on your selection.
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Platform Category *</InputLabel>
                <Select
                  value={selectedPlatform}
                  label="Platform Category *"
                  onChange={(e) => setSelectedPlatform(e.target.value)}
                >
                  {platformCategories.map((pc) => (
                    <MenuItem key={pc.value} value={pc.value}>
                      <Box>
                        <Typography variant="body1">{pc.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {pc.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        )}

        {(() => {
          // Build cumulative question offsets for global numbering
          const categoryEntries = Object.entries(questions.categories);
          let questionOffset = 0;
          return categoryEntries.map(([categoryKey, categoryData]) => {
            const startNum = questionOffset + 1;
            questionOffset += categoryData.questions.length;
            return (
          <Accordion
            key={categoryKey}
            expanded={expandedCategory === categoryKey}
            onChange={(_, isExpanded) => setExpandedCategory(isExpanded ? categoryKey : false)}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6">{categoryData.name}</Typography>
                <Chip 
                  label={`${categoryData.questions.filter(q => userAnswers[q.id]?.selectedOptions.length > 0).length}/${categoryData.questions.length}`}
                  size="small"
                  color={categoryData.questions.filter(q => userAnswers[q.id]?.selectedOptions.length > 0).length === categoryData.questions.length ? 'success' : 'default'}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {categoryData.description}
              </Typography>
              
              {categoryData.questions.map((question, qIdx) => (
                <Card key={question.id} sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      <Box component="span" sx={{ fontWeight: 700, color: 'primary.main', mr: 1 }}>
                        Q{startNum + qIdx}.
                      </Box>
                      {question.question}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {question.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: 1, bgcolor: 'info.main', borderRadius: 1, opacity: 0.85 }}>
                      <InfoOutlinedIcon sx={{ fontSize: 18, color: 'info.contrastText' }} />
                      <Typography variant="caption" sx={{ color: 'info.contrastText' }}>
                        Select all options that are relevant to your current environment or aspirations. You may choose more than one.
                      </Typography>
                    </Box>
                    <FormGroup>
                      {question.options.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          control={
                            <Checkbox
                              checked={userAnswers[question.id]?.selectedOptions.includes(option.value) || false}
                              onChange={() => handleOptionToggle(question.id, option.value)}
                            />
                          }
                          label={`${option.value}: ${option.label}`}
                        />
                      ))}
                    </FormGroup>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={4}
                      placeholder="Add any additional context or details not captured by the options above..."
                      value={userAnswers[question.id]?.comment || ''}
                      onChange={(e) => handleCommentChange(question.id, e.target.value)}
                      variant="outlined"
                      size="small"
                      sx={{ mt: 1.5 }}
                    />
                  </CardContent>
                </Card>
              ))}
            </AccordionDetails>
          </Accordion>
            );
          });
        })()}

        <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button onClick={onBack} variant="outlined">
            Back to Selection
          </Button>
          <Button
            onClick={saveProgress}
            variant="outlined"
            color="secondary"
            disabled={saving || answered === 0}
            startIcon={<SaveIcon />}
          >
            {saving ? 'Saving...' : 'Save & Resume Later'}
          </Button>
          <Button
            onClick={runAssessment}
            variant="contained"
            disabled={answered === 0}
            startIcon={<PlayArrowIcon />}
          >
            {isArchitecture
              ? `Generate Blueprint Architecture (${answered} selections)`
              : `Run Assessment (${answered} answers)`}
          </Button>
          {saveMessage && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {saveMessage}
            </Typography>
          )}
          {lastSaved && !saveMessage && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              Last saved: {lastSaved}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const renderResults = () => {
    if (!result) return null;

    // Handle regular assessment results (including architecture generation)
    const assessmentResult = result.result;

    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          {isArchitecture ? 'Blueprint Architecture' : 'Assessment Results'}
        </Typography>

        {/* Score card — only for regular assessments, not architecture */}
        {!isArchitecture && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Overall Score: {assessmentResult.domainScore?.toFixed(1) || 'N/A'}/5.0
            </Typography>
            {assessmentResult.domainScore && (
              <LinearProgress 
                variant="determinate" 
                value={(assessmentResult.domainScore / 5) * 100}
                sx={{ height: 8, borderRadius: 4 }}
              />
            )}
          </CardContent>
        </Card>
        )}

        {/* Mermaid Architecture Diagram — rendered visually */}
        {assessmentResult.mermaidDiagram && (
          <MermaidDiagram diagram={assessmentResult.mermaidDiagram} title="Architecture Diagram" />
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {isArchitecture ? 'Architecture Overview' : 'Executive Summary'}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
              {assessmentResult.summary || 'No summary available'}
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              {isArchitecture ? 'Design Recommendations' : 'Key Recommendations'}
            </Typography>
            {assessmentResult.recommendations?.map((rec: string, index: number) => (
              <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                • {rec}
              </Typography>
            )) || <Typography variant="body2">No recommendations available</Typography>}
          </CardContent>
        </Card>

        {/* Detailed per-question analysis — only for regular assessments */}
        {!isArchitecture && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Detailed Analysis
            </Typography>
            {assessmentResult.items?.map((item: any, index: number) => (
              <Box key={item.id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Q{index + 1}: {item.question}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Score:</strong> {item.score}/5 - <strong>Risk:</strong> {item.riskLevel}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Analysis:</strong> {item.rationale}
                </Typography>
                <Typography variant="body2">
                  <strong>Recommendation:</strong> {item.recommendation}
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
        )}

        {/* Architecture component details — only for architecture generation */}
        {isArchitecture && assessmentResult.items && assessmentResult.items.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Component Details
            </Typography>
            {assessmentResult.items.map((item: any, index: number) => (
              <Box key={item.id || index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {item.question}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {item.rationale}
                </Typography>
                {item.recommendation && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Note:</strong> {item.recommendation}
                </Typography>
                )}
              </Box>
            ))}
          </CardContent>
        </Card>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {isArchitecture ? 'Generation Metadata' : 'Assessment Metadata'}
            </Typography>
              <Typography variant="body2">
                <strong>Agent:</strong> {assessmentResult.metadata?.agentName}
              </Typography>
              <Typography variant="body2">
                <strong>Completed:</strong> {assessmentResult.metadata?.timestamp ? new Date(assessmentResult.metadata.timestamp).toLocaleString() : 'N/A'}
              </Typography>
            </CardContent>
          </Card>

        {smeSubmitError && (
          <Alert severity="error" sx={{ mb: 2 }}>{smeSubmitError}</Alert>
        )}

        {hideExcelExport && smeSubmitStatus !== 'submitted' && (
          <Alert severity="info" icon={false} sx={{ mb: 2, border: '1px solid #0096D6', backgroundColor: 'rgba(0,150,214,0.06)' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Report Delivery Process
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please send your assessment to the SME for expert review. Your final report (PowerPoint) 
              will be available for download on the Final Reports page after the review and playback session 
              are completed.{' '}
              <a href="/client/reports" style={{ color: '#00338D', fontWeight: 600 }}>Click here to monitor your assessment report readiness</a>.
            </Typography>
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button onClick={onBack} variant="outlined">
            Start New Assessment
          </Button>
          {!hideExcelExport && (
          <Button
            onClick={exportToExcel}
            variant="contained"
            startIcon={<GetAppIcon />}
          >
            Export to Excel
          </Button>
          )}
          <Button
            onClick={submitToSME}
            variant="contained"
            color="primary"
            startIcon={smeSubmitStatus === 'submitted' ? <CheckCircleIcon /> : <SendIcon />}
            sx={{ backgroundColor: smeSubmitStatus === 'submitted' ? '#4caf50' : '#00338D' }}
            disabled={smeSubmitStatus === 'submitting' || smeSubmitStatus === 'submitted'}
          >
            {smeSubmitStatus === 'submitting' ? 'Submitting...' :
             smeSubmitStatus === 'submitted' ? 'Sent to SME for Review' :
             'Send to SME for Review'}
          </Button>
        </Box>
      </Box>
    );
  };

  const renderHumanValidation = () => {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Draft Report Submitted for SME Review
        </Typography>
        
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="h6">Assessment draft sent to SME for review</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Your assessment report has been submitted to the KPMG SME team. You will receive an email 
            and in-app notification when the review is complete and the report is ready for playback.
          </Typography>
        </Alert>
        
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              What Happens Next
            </Typography>
            
            <Box sx={{ ml: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Step 1:</strong> <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main', verticalAlign: 'middle', mr: 0.5 }} /> Assessment completed and draft report generated
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Step 2:</strong> <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main', verticalAlign: 'middle', mr: 0.5 }} /> Draft sent to SME for expert review
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Step 3:</strong> SME reviews and validates findings
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Step 4:</strong> Engagement Manager approves the report
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Step 5:</strong> PowerPoint presentation generated for playback session
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Step 6:</strong> Playback meeting scheduled — you review the report with KPMG team
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Step 7:</strong> Final report delivered for download (30-day retention)
              </Typography>
            </Box>
          </CardContent>
        </Card>
        
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Assessment Summary
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Organization:</strong> {organizationInfo.organizationName}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Contact Email:</strong> {organizationInfo.contactEmail}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Industry:</strong> {organizationInfo.industry}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Assessment Domain:</strong> {domain}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Assessment ID:</strong> {result?.assessmentId}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Date:</strong> {new Date().toLocaleDateString()}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        
        <Alert severity="info" icon={false} sx={{ mb: 2, border: '1px solid #00338D', backgroundColor: 'rgba(0,51,141,0.04)' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            When will I receive my final report?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your final PowerPoint report will be available for download on the Final Reports page 
            once the SME review is complete, the Engagement Manager has approved the report, and the playback 
            session has been conducted.{' '}
            <a href="/client/reports" style={{ color: '#00338D', fontWeight: 600 }}>Click here to monitor your assessment report readiness</a>.
          </Typography>
        </Alert>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button onClick={onBack} variant="outlined">
            Start New Assessment
          </Button>
        </Box>
      </Box>
    );
  };

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={onBack} variant="outlined">
          Back to Selection
        </Button>
      </Box>
    );
  }

  const renderOrganizationForm = () => {
    const industryOptions = [
      'Technology', 'Healthcare', 'Financial Services', 'Manufacturing', 
      'Retail', 'Education', 'Government', 'Non-profit', 'Consulting', 
      'Energy', 'Transportation', 'Media & Entertainment', 'Other'
    ];

    const sizeOptions = [
      'Startup (1-10 employees)',
      'Small (11-50 employees)', 
      'Medium (51-200 employees)',
      'Large (201-1000 employees)',
      'Enterprise (1000+ employees)'
    ];

    const experienceOptions = [
      'Less than 1 year',
      '1-2 years',
      '3-5 years', 
      '6-10 years',
      'More than 10 years'
    ];

    return (
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Organization Information
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please provide your organization details to personalize the assessment.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Organization Name"
                fullWidth
                value={organizationInfo.organizationName}
                onChange={(e) => handleOrganizationInfoChange('organizationName', e.target.value)}
                InputProps={{ readOnly: !!organizationData?.organizationName }}
                helperText={organizationData?.organizationName ? "Auto-populated from your profile" : "Enter your organization name"}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Contact Email"
                type="email"
                fullWidth
                value={organizationInfo.contactEmail}
                onChange={(e) => handleOrganizationInfoChange('contactEmail', e.target.value)}
                InputProps={{ readOnly: !!organizationData?.userEmail }}
                helperText={organizationData?.userEmail ? "Auto-populated from your profile" : "Enter your contact email"}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!organizationInfo.industry}>
                <InputLabel>Industry *</InputLabel>
                <Select
                  value={organizationInfo.industry}
                  label="Industry *"
                  onChange={(e) => handleOrganizationInfoChange('industry', e.target.value)}
                >
                  {industryOptions.map((industry) => (
                    <MenuItem key={industry} value={industry}>
                      {industry}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!organizationInfo.organizationSize}>
                <InputLabel>Organization Size *</InputLabel>
                <Select
                  value={organizationInfo.organizationSize}
                  label="Organization Size *"
                  onChange={(e) => handleOrganizationInfoChange('organizationSize', e.target.value)}
                >
                  {sizeOptions.map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!organizationInfo.yearsOfExperience}>
                <InputLabel>Years of Experience *</InputLabel>
                <Select
                  value={organizationInfo.yearsOfExperience}
                  label="Years of Experience *"
                  onChange={(e) => handleOrganizationInfoChange('yearsOfExperience', e.target.value)}
                >
                  {experienceOptions.map((exp) => (
                    <MenuItem key={exp} value={exp}>
                      {exp}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
            <Button onClick={onBack} variant="outlined">
              Back to Selection
            </Button>
            <Button 
              onClick={proceedToQuestions}
              variant="contained"
              disabled={!isOrganizationInfoComplete()}
              sx={{ minWidth: 200 }}
            >
              Continue to Questions
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {category} - {domain}
        </Typography>
        
        <Stepper activeStep={currentStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {loading && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress />
          <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
            {currentStep === 1 && (isArchitecture ? 'Loading architecture configuration...' : 'Loading assessment questions...')}
            {currentStep === 3 && !isPaused && (isArchitecture ? 'Generating blueprint architecture with AI specialist...' : 'Running assessment with AI specialist...')}
            {currentStep === 3 && isPaused && (isArchitecture ? 'Generation paused — click resume to continue' : 'Assessment paused - Click resume to continue')}
          </Typography>
          
          {currentStep === 3 && (
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              {!isPaused ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PauseIcon />}
                  onClick={pauseAssessment}
                  sx={{ minWidth: 100 }}
                >
                  Pause
                </Button>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<PlayCircleOutlineIcon />}
                  onClick={resumeAssessment}
                  sx={{ minWidth: 100 }}
                >
                  Resume
                </Button>
              )}
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<StopIcon />}
                onClick={stopAssessment}
                sx={{ minWidth: 100 }}
              >
                Stop
              </Button>
            </Box>
          )}
        </Box>
      )}

      {currentStep === 0 && renderOrganizationForm()}
      {currentStep === 2 && renderQuestions()}
      {currentStep === 4 && renderResults()}
      {currentStep === 5 && renderHumanValidation()}
    </Box>
  );
};

export default AssessmentFlow;
